import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, ShieldCheck, Copy, Hash, Calendar, User, FileText } from "lucide-react";
import { toast } from "sonner";
import { LogoIcon } from "@/components/Logo";

export default function Verify() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/verify/${id}`;
    fetch(url)
      .then((r) => r.json())
      .then((d) => setData(d))
      .catch(() => setData({ ok: false, error: "No se pudo conectar" }))
      .finally(() => setLoading(false));
  }, [id]);

  const copy = async (t) => {
    await navigator.clipboard.writeText(t);
    toast.success("Copiado");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBFAF6] flex items-center justify-center text-[#7A786F]">
        Comprobando certificado…
      </div>
    );
  }

  const ok = data?.ok;

  return (
    <div className="min-h-screen bg-[#FBFAF6] flex flex-col" data-testid="verify-page">
      {/* Top bar */}
      <header className="px-6 py-5 flex items-center justify-between border-b border-[#E5E2D8] bg-white">
        <div className="flex items-center gap-2.5">
          <LogoIcon size={36} />
          <div className="leading-tight">
            <div className="font-heading font-medium text-[18px] tracking-tight">
              <span className="text-[#2C2C2A]">eks</span>
              <span className="text-[#D85A30]">vidi</span>
            </div>
            <div className="text-[10px] text-[#7A786F] tracking-wider uppercase">Verificación de certificado</div>
          </div>
        </div>
        <a href="/" className="text-xs text-[#7A786F] hover:text-[#2C2C2A]">Ir a Eksvidi</a>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-10">
        {!ok ? (
          <div className="bg-white border border-[#E5E2D8] rounded-[16px] p-8 text-center" data-testid="verify-error">
            <AlertCircle className="h-12 w-12 text-[#A32D2D] mx-auto mb-3" />
            <h1 className="font-heading text-2xl font-bold text-[#2C2C2A]">Certificado no encontrado</h1>
            <p className="text-[#7A786F] mt-2">{data?.error || "El identificador no corresponde a ningún certificado emitido por Eksvidi."}</p>
            <code className="block text-[11px] text-[#7A786F] mt-4 font-mono break-all">{id}</code>
          </div>
        ) : (
          <>
            {/* Status banner */}
            <div className="bg-white border border-[#E5E2D8] rounded-[16px] overflow-hidden">
              <div className="bg-[#1D9E75] text-white px-6 py-4 flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6" />
                <div>
                  <div className="font-heading font-semibold text-lg">Certificado válido</div>
                  <div className="text-[12px] text-white/90">Documento emitido por Eksvidi y registrado de forma íntegra.</div>
                </div>
              </div>

              <div className="px-6 py-5 space-y-4">
                <Field icon={FileText} label="Expediente" value={data.expediente_num} />
                <Field icon={User} label="Asegurado" value={data.asegurado_nombre} />
                <Field icon={ShieldCheck} label="Perito" value={data.perito_name} />
                <Field icon={Calendar} label="Finalización" value={fmt(data.finalized_at)} />
                <Field icon={Calendar} label="Consentimiento RGPD" value={fmt(data.consent_at)} />
                <Field icon={FileText} label="Fotografías" value={`${data.photo_count} ${data.photo_count === 1 ? "imagen" : "imágenes"}`} />
              </div>
            </div>

            {/* Hash card */}
            <div className="bg-white border border-[#E5E2D8] rounded-[16px] mt-6 p-6" data-testid="hash-card">
              <div className="flex items-center gap-2 mb-3">
                <Hash className="h-4 w-4 text-[#D85A30]" />
                <h2 className="font-heading font-semibold text-[#2C2C2A]">Hash SHA-256 del manifiesto</h2>
              </div>
              <p className="text-[12px] text-[#7A786F] mb-3">
                Este hash se calcula sobre los datos canónicos del expediente (identificadores, fotos con su propio SHA-256, geolocalización y timestamps). Si los datos cambiasen, el hash sería distinto.
              </p>
              <div className="bg-[#FBFAF6] border border-[#E5E2D8] rounded-[12px] p-3 flex items-center justify-between gap-3">
                <code className="text-[12px] font-mono text-[#2C2C2A] break-all" data-testid="manifest-hash">
                  {data.manifest_sha256 || "—"}
                </code>
                {data.manifest_sha256 && (
                  <button
                    onClick={() => copy(data.manifest_sha256)}
                    className="h-8 w-8 rounded-[10px] border border-[#E5E2D8] hover:bg-[#FAECE7] flex items-center justify-center shrink-0"
                    data-testid="copy-hash-btn"
                    aria-label="Copiar hash"
                  >
                    <Copy className="h-3.5 w-3.5 text-[#7A786F]" />
                  </button>
                )}
              </div>
            </div>

            <p className="text-[11px] text-[#7A786F] mt-6 text-center px-4">
              Eksvidi es una plataforma de video peritación de Ekspertium. La verificación de este documento es pública y no requiere autenticación.
            </p>
          </>
        )}
      </main>

      <footer className="px-6 py-5 text-center text-[10px] text-[#7A786F] border-t border-[#E5E2D8]">
        © Eksvidi · by Ekspertium
      </footer>
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-1">
      <div className="h-8 w-8 rounded-[10px] bg-[#FAECE7] flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-[#D85A30]" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-[#7A786F] font-semibold">{label}</div>
        <div className="text-sm text-[#2C2C2A] font-medium break-words">{value || "—"}</div>
      </div>
    </div>
  );
}

function fmt(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" });
  } catch { return s; }
}
